import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data-loading", "className", "failed", "grow", "label", "labelCompletedText", "layout", "minHeight", "onResultDisplayFinish", "resultAnimationDuration", "resultIcon", "showLabel", "showResult", "size", "style", "use"];
/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-implied-eval */

import classNames from 'classnames';
import { OLAF } from 'HubStyleTokens/colors';
import { BASE_ICON_SPACING_X } from 'HubStyleTokens/sizes';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import styled, { css, keyframes } from 'styled-components';
import UIIconHolder from '../icon/UIIconHolder';
import { isIE11 } from '../utils/BrowserTest';
import lazyEval from '../utils/lazyEval';
import memoWithDisplayName from '../utils/memoWithDisplayName';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import { useFoundationsReadinessTracking } from '../utils/useFoundationsReadinessTracking';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { ICON_COLOR_MAP, ICON_SIZE_MAP, SIZE_OPTIONS, USES, USE_INDICATOR_COLORS, USE_COLORS_RECORD } from './LoadingConstants';

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSpinnerSize = (sizeName = 'sm') => {
  return SIZE_OPTIONS[sizeName];
};
const privateSpin = keyframes(["100%{transform:rotate(360deg)}"]);
const privateDash = keyframes(["0%{stroke-dasharray:1,150;stroke-dashoffset:0;}50%{stroke-dasharray:90,150;stroke-dashoffset:-50;}100%{stroke-dasharray:90,150;stroke-dashoffset:-140;}"]);
const SpinnerOuter = styled.div.withConfig({
  displayName: "UILoadingSpinner__SpinnerOuter"
})(["position:relative;align-items:center;color:", ";display:flex;flex-direction:", ";justify-content:", ";", ";"], ({
  use
}) => use === 'on-dark' ? OLAF : 'currentColor', ({
  layout
}) => layout === 'centered' && 'column', ({
  layout
}) => layout === 'centered' && 'center', ({
  grow
}) => grow ? `width: 100%; height: 100%;` : 'margin: 8px');
const SpinnerInner = styled.div.withConfig({
  displayName: "UILoadingSpinner__SpinnerInner"
})(["align-items:center;display:flex;height:", ";justify-content:center;width:", ";"], props => getSpinnerSize(props.size), props => getSpinnerSize(props.size));
const SpinnerLabel = styled.span.withConfig({
  displayName: "UILoadingSpinner__SpinnerLabel"
})(["margin-top:", ";&:not(:empty){margin-left:", ";margin-right:", ";}"], props => props.layout === 'centered' && BASE_ICON_SPACING_X, BASE_ICON_SPACING_X, BASE_ICON_SPACING_X);
const ResultSpinner = styled.div.withConfig({
  displayName: "UILoadingSpinner__ResultSpinner"
})(["align-items:center;display:flex;justify-content:center;opacity:0;position:absolute;transform:scale(0.7);transition:opacity 0.2s cubic-bezier(0.42,0,0.58,1) 0.1s,transform 0.3s cubic-bezier(0.2,0.9,0.3,2) 0.1s;", ""], ({
  showResult
}) => showResult && `opacity: 1;
    position: relative;
    transform: scale(1);
  `);
const SpinnerWrapper = styled.div.withConfig({
  displayName: "UILoadingSpinner__SpinnerWrapper"
})(["opacity:1;transition:opacity 0.2s cubic-bezier(0.42,0,0.58,1),transform 0.2s cubic-bezier(0.89,0.03,0.68,0.22);", " ", ""], isIE11() && css(["animation:", " 2s linear infinite;"], privateSpin), ({
  showResult
}) => showResult && `margin-left: -1px;
    opacity: 0;
    transform: scale(.7);
    width: 1px;
  `);
const Ring = styled.svg.withConfig({
  displayName: "UILoadingSpinner__Ring"
})(["display:block;max-width:100%;"]);
const RingPath = styled.circle.withConfig({
  displayName: "UILoadingSpinner__RingPath"
})(["animation:", " 2s cubic-bezier(0.42,0,0.58,1) infinite,", " 2s linear infinite;stroke-dasharray:none;stroke-dashoffset:0;stroke-linecap:round;transform-origin:center;", ""], privateDash, privateSpin, isIE11() && `stroke-dasharray: 50, 150;`);
const getComputedStyle = (style, grow, minHeight) => {
  if (minHeight) {
    const computedStyle = Object.assign({}, style, {
      minHeight
    });
    if (!grow) computedStyle.height = '1rem';
    return computedStyle;
  }
  return style;
};
const resultIconStyles = {
  lineHeight: 'inherit',
  verticalAlign: 'middle'
};
const UILoadingSpinner = memoWithDisplayName('UILoadingSpinner', _ref => {
  let {
      'data-loading': dataLoading,
      className,
      failed = false,
      grow = false,
      label = () => I18n.text('ui.UILoadingSpinner.busyText'),
      labelCompletedText = () => I18n.text('ui.UILoadingSpinner.completedText'),
      layout,
      minHeight,
      onResultDisplayFinish = emptyFunction,
      resultAnimationDuration = 1500,
      resultIcon = 'success',
      showLabel = false,
      showResult = false,
      size = 'sm',
      style,
      use = 'link'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  useFoundationsReadinessTracking();
  const resultDisplayTimeoutRef = useRef(undefined);
  useEffect(() => {
    if (!showResult) return undefined;
    resultDisplayTimeoutRef.current = setTimeout(onResultDisplayFinish, resultAnimationDuration);
    return () => {
      clearTimeout(resultDisplayTimeoutRef.current);
    };
  }, [onResultDisplayFinish, resultAnimationDuration, showResult]);
  const computedLayout = layout || (grow ? 'centered' : 'inline');
  const computedLabelText = showResult && !failed ? lazyEval(labelCompletedText) : lazyEval(label);
  const RenderedLabel = showLabel ? SpinnerLabel : 'span';
  const renderedLabelProps = {
    children: computedLabelText
  };
  if (showLabel) {
    renderedLabelProps.layout = computedLayout;
  } else {
    renderedLabelProps.className = 'sr-only';
  }
  const shorthandSize = toShorthandSize(size);
  const spinnerSizePx = parseInt(getSpinnerSize(shorthandSize), 10);
  return /*#__PURE__*/_jsxs(SpinnerOuter, Object.assign({}, rest, {
    className: classNames('private-spinner', className),
    style: getComputedStyle(style, grow, minHeight),
    "data-loading": dataLoading !== undefined ? dataLoading : !showResult,
    layout: computedLayout,
    grow: grow,
    use: use,
    children: [/*#__PURE__*/_jsxs(SpinnerInner, {
      size: shorthandSize,
      children: [/*#__PURE__*/_jsx(SpinnerWrapper, {
        showResult: showResult,
        children: /*#__PURE__*/_jsxs(Ring, {
          height: spinnerSizePx,
          width: spinnerSizePx,
          viewBox: "0 0 50 50",
          stroke: USE_COLORS_RECORD[use],
          children: [/*#__PURE__*/_jsx("circle", {
            cx: "25",
            cy: "25",
            r: "22.5",
            fill: "none",
            strokeWidth: "5"
          }), /*#__PURE__*/_jsx(RingPath, {
            cx: "25",
            cy: "25",
            r: "22.5",
            fill: "none",
            stroke: USE_INDICATOR_COLORS[use],
            strokeWidth: "5"
          })]
        })
      }), resultIcon && !failed && /*#__PURE__*/_jsx(ResultSpinner, {
        showResult: showResult,
        children: /*#__PURE__*/_jsx(UIIconHolder, {
          innerStyles: resultIconStyles,
          name: resultIcon,
          color: ICON_COLOR_MAP[use],
          size: ICON_SIZE_MAP[shorthandSize] || ICON_SIZE_MAP['sm']
        })
      })]
    }), /*#__PURE__*/_jsx(RenderedLabel, Object.assign({}, renderedLabelProps))]
  }));
});
const propTypes = {
  'data-loading': PropTypes.bool,
  failed: PropTypes.bool,
  showResult: PropTypes.bool,
  label: createLazyPropType(PropTypes.node),
  labelCompletedText: createLazyPropType(PropTypes.node),
  layout: PropTypes.oneOf(['inline', 'centered']),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  grow: PropTypes.bool,
  resultAnimationDuration: PropTypes.number,
  resultIcon: PropTypes.string,
  use: PropTypes.oneOf(USES),
  showLabel: PropTypes.bool,
  size: propTypeForSizes(['xs', 'sm', 'md']),
  onResultDisplayFinish: PropTypes.func
};
UILoadingSpinner.propTypes = propTypes;
const defaultProps = {
  failed: false,
  grow: false,
  label: () => I18n.text('ui.UILoadingSpinner.busyText'),
  labelCompletedText: () => I18n.text('ui.UILoadingSpinner.completedText'),
  showResult: false,
  resultAnimationDuration: 1500,
  resultIcon: 'success',
  role: 'status',
  onResultDisplayFinish: emptyFunction,
  showLabel: false,
  size: 'sm',
  use: 'link'
};
/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UILoadingSpinner.defaultProps = defaultProps;
export default UILoadingSpinner;